import { createColumnHelper } from '@tanstack/react-table';
import * as z from 'zod';

import { formatNumber } from '@/shared/helpers';

import type { BracketConfigDraft } from './bracket-draft';

const columnHelper = createColumnHelper<BracketConfigDraft>();

/**
 * Per-row validation for the inline editable bracket table: a weight greater than 0 and a cubic
 * allowance that is either empty or at least 0. Uniqueness across weights is a cross-row rule the
 * per-row schema can't see, so it is enforced at save time (see the table's `onSaveRow`).
 */
export const bracketRowSchema = z.object({
  bracket: z.number('Enter a weight greater than 0').positive(),
  cubicAllowance: z.number().nonnegative().nullable(),
});

export type BracketRowValues = z.output<typeof bracketRowSchema>;

function formatOptionalNumber(value: number | null | undefined, precision = 2) {
  if (value === null || value === undefined) return undefined;
  return formatNumber(value, precision);
}

export const bracketColumns = [
  columnHelper.display({
    id: 'position',
    header: '#',
    cell: ({ row }) => row.index + 1,
  }),
  columnHelper.accessor('bracket', {
    header: 'Bracket weight (kg)',
    cell: ({ getValue }) => formatOptionalNumber(getValue()),
    meta: {
      align: 'right',
      editable: { label: 'Bracket weight', editor: { type: 'number', min: 0, step: 'any' } },
    },
  }),
  columnHelper.accessor('cubicAllowance', {
    header: 'Cubic allowance (m³)',
    cell: ({ getValue }) => formatOptionalNumber(getValue()),
    meta: {
      align: 'right',
      editable: { label: 'Cubic allowance', editor: { type: 'number', min: 0, step: 'any' } },
    },
  }),
];
